<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupGirerdCard from "@/medication-checkup/components/MedicationCheckupGirerdCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {GirerdAnswer, GirerdView} from "@/medication-checkup/interfaces/girerd";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useGirerdAnswers} from "@/medication-checkup/composables/useGirerdAnswers";
import {useMissingAnswers} from "@/medication-checkup/composables/useMissingAnswers";

/**
 * The scene of the Girerd block: the adherence of the patient, asked over the
 * treatments the collection just retained.
 *
 * Everything it shows is served by the handler: the transition line, the six
 * questions, the retained treatments, and — once validated — the answers
 * given with the reading of the score and the closing line. Answering is the
 * scene's own state, as checking lines is in the collection: the closing
 * action, held until the six are answered, submits them all at once.
 */

const props = defineProps<SceneProps<GirerdView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, closing, acted, act: submit} = useScene(
    props,
    {card: true}
);

const questions = computed(() => view.value?.questions ?? []);

/**
 * The answers given so far, by question ref — local until the closing
 * action, held until the six are answered, submits them. Resets with the
 * scene, as the selection of the collection does.
 */
const {answers, missing, answer} = useGirerdAnswers<GirerdAnswer>(questions);

const root = ref<HTMLElement | null>(null);

const {blocked, flagged, warning, flag} = useMissingAnswers(missing, root);

/**
 * Take the closing action, submitting the six answers.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    answers: questions.value.map(question => ({
        question_ref: question.ref,
        ...answers.value[question.ref]
    }))
});
</script>

<template>
  <div ref="root" class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupGirerdCard v-if="view" :view="view" frozen />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupGirerdCard
        v-if="cardShown && view"
        :view="view"
        :answers="answers"
        :flagged="flagged"
        class="animate-message-in"
        @answer="answer"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :blocked="blocked"
        :warning="warning"
        @act="act"
        @blocked="flag"
      />
    </template>
  </div>
</template>
